import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import {
  Card,
  Chip,
  Disclosure,
  EmptyState,
  ErrorNote,
  Hash,
  Kv,
  KvList,
  Stack,
} from "../ui/primitives";
import type { Tone } from "../ui/tone";
import { IconApprovals, IconArrowRight } from "../ui/icons";

/** Date-only. Full ISO timestamps live in the Technical details panel. */
function shortWhen(iso: string): string {
  return iso.slice(0, 10);
}
import { AuthRequired } from "../components/AuthRequired";
import { apiCall, ApiHttpError, RouteNotFoundError } from "../api/client";

// Approvals list — session-auth BFF flow (PR #135 BE).
//
// Loads `GET /v2/admin/bff/approvals` which is bearer-session +
// trusted-device gated. NO HMAC in the browser. The list response
// includes the redacted payload JSON so we can render a meaningful
// per-proposal summary row without a second RTT.

interface BffProposalView {
  id: string;
  actionType: string;
  actionDigest: string;
  payloadHash: string;
  payload: unknown;
  thresholdRequired: number;
  status: "proposed" | "approved" | "rejected" | "expired" | "executed" | "cancelled";
  proposedByOwnerId: string;
  expiresAt: string;
  createdAt: string;
  approvedAt: string | null;
  rejectedAt: string | null;
  executedAt: string | null;
  cancelledAt: string | null;
  executedTxHash: string | null;
  executedBlockNumber: number | null;
  executedByWalletAddress: string | null;
}

interface BffApprovalsListResponse {
  ok: boolean;
  status: string | null;
  proposals: BffProposalView[];
  sessionOwnerIdentityId: string;
  sessionWalletAddress: string;
  requestId: string;
}

type StatusFilter = "" | "proposed" | "approved" | "rejected" | "expired" | "executed" | "cancelled";

function summariseActionType(actionType: string): string {
  switch (actionType) {
    case "read":
      return "Read";
    case "low_config":
      return "Config (low)";
    case "high_config":
      return "Config (high)";
    case "money_movement":
      return "Money movement";
    case "production_deploy":
      return "Production deploy";
    default:
      return actionType;
  }
}

function summariseStatus(status: BffProposalView["status"]): { label: string; tone: Tone } {
  switch (status) {
    case "proposed":
      return { label: "Proposed", tone: "warn" };
    case "approved":
      return { label: "Approved", tone: "ok" };
    case "rejected":
      return { label: "Rejected", tone: "danger" };
    case "expired":
      return { label: "Expired", tone: "neutral" };
    case "executed":
      return { label: "Executed", tone: "info" };
    case "cancelled":
      return { label: "Cancelled", tone: "neutral" };
  }
}

/** How much care an action needs, derived from the REAL `actionType` the
 *  backend already sends.
 *
 *  The operator brief asked for a "Risk" column. There is no risk field on a
 *  proposal and inventing a score would be worse than useless on a screen that
 *  authorises money movement, so this is the honest version of that column:
 *  the action's own class, which the multisign scheme already treats as the
 *  thing that decides how much scrutiny applies. */
function actionClass(actionType: string): { label: string; tone: Tone } {
  switch (actionType) {
    case "money_movement":
      return { label: "MOVES FUNDS", tone: "danger" };
    case "production_deploy":
      return { label: "PRODUCTION", tone: "danger" };
    case "high_config":
      return { label: "HIGH CONFIG", tone: "warn" };
    case "low_config":
      return { label: "LOW CONFIG", tone: "neutral" };
    case "read":
      return { label: "READ ONLY", tone: "neutral" };
    default:
      return { label: actionType.replace(/_/g, " ").toUpperCase(), tone: "neutral" };
  }
}

// Render a short human-readable cue from the payload. Display only;
// never trusted for security decisions. The BE has already redacted
// banned-field values before sending the row.
function summarisePayload(p: unknown): string {
  if (p === null || typeof p !== "object") return "";
  const o = p as Record<string, unknown>;
  if (o["action"] === "withdrawTreasury") {
    const amt =
      typeof o["amountHumanReadable"] === "string"
        ? (o["amountHumanReadable"] as string)
        : typeof o["amountWei"] === "string"
          ? `${o["amountWei"] as string} wei`
          : "?";
    const dst = typeof o["destination"] === "string" ? (o["destination"] as string) : "?";
    return `withdrawTreasury ${amt} → ${dst}`;
  }
  if (o["action"] === "escrow_refund") {
    const domain =
      typeof o["domain"] === "string" ? (o["domain"] as string) : "?";
    const amt =
      typeof o["amountBaseUnits"] === "string"
        ? (o["amountBaseUnits"] as string)
        : "?";
    const dst =
      typeof o["refundToPayer"] === "string"
        ? (o["refundToPayer"] as string)
        : "?";
    return `escrow_refund ${domain} ${amt} base → ${dst}`;
  }
  if (typeof o["action"] === "string") return String(o["action"]);
  return "";
}

function ApprovalsInner() {
  const [data, setData] = useState<BffApprovalsListResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [status, setStatus] = useState<StatusFilter>("proposed");

  const load = useCallback(async (filter: StatusFilter) => {
    setError(null);
    setLoading(true);
    try {
      const query: Record<string, string | undefined> = {};
      if (filter !== "") query["status"] = filter;
      const j = await apiCall<BffApprovalsListResponse>("/v2/admin/bff/approvals", {
        query,
      });
      setData(j);
    } catch (e) {
      if (e instanceof RouteNotFoundError) {
        setError("Route /v2/admin/bff/approvals not registered. Check the BE deploy.");
      } else if (e instanceof ApiHttpError) {
        setError(`HTTP ${e.status} from /v2/admin/bff/approvals.`);
      } else if (e instanceof Error) {
        setError(e.message);
      } else {
        setError("Unknown error.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(status);
  }, [load, status]);

  return (
    <div>
      <div style={{ display: "flex", gap: 8, alignItems: "center", margin: "12px 0" }}>
        <label htmlFor="status-filter" style={{ fontSize: 12, color: "var(--fg-dim)" }}>
          Status:
        </label>
        <select
          id="status-filter"
          value={status}
          onChange={(e) => setStatus(e.target.value as StatusFilter)}
          style={{ padding: "4px 8px", fontSize: 13 }}
        >
          <option value="proposed">Proposed</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="expired">Expired</option>
          <option value="executed">Executed</option>
          <option value="cancelled">Cancelled</option>
          <option value="">All</option>
        </select>
        <button
          type="button"
          onClick={() => void load(status)}
          disabled={loading}
          style={{ padding: "4px 12px", fontSize: 13 }}
        >
          {loading ? "Loading…" : "Refresh"}
        </button>
        <Link
          to="/approvals/history"
          style={{ fontSize: 12, marginLeft: "auto", color: "var(--accent, #2c7be5)" }}
        >
          History (executed / rejected / expired) →
        </Link>
      </div>

      {error && (
        <div style={{ margin: "12px 0" }}>
          <ErrorNote onRetry={() => void load(status)}>{error}</ErrorNote>
        </div>
      )}

      {data && (
        <>
          {data.proposals.length === 0 ? (
            <EmptyState title="Nothing needs your approval" icon={<IconApprovals size={22} />}>
              {status === ""
                ? "No proposals exist."
                : `No proposals with status "${status}".`}
            </EmptyState>
          ) : (
            <Stack gap={10}>
              {data.proposals.map((p) => {
                const st = summariseStatus(p.status);
                const cls = actionClass(p.actionType);
                const what = summarisePayload(p.payload);
                return (
                  <Card key={p.id}>
                    <div
                      style={{
                        display: "flex",
                        gap: 14,
                        alignItems: "flex-start",
                        flexWrap: "wrap",
                      }}
                    >
                      <div style={{ flex: "1 1 320px", minWidth: 0 }}>
                        <div
                          style={{
                            display: "flex",
                            gap: 8,
                            alignItems: "center",
                            flexWrap: "wrap",
                            marginBottom: 4,
                          }}
                        >
                          <span style={{ fontSize: 15, fontWeight: 640 }}>
                            {summariseActionType(p.actionType)}
                          </span>
                          <Chip tone={cls.tone} solid size="sm">
                            {cls.label}
                          </Chip>
                          <Chip tone={st.tone} solid size="sm">
                            {st.label.toUpperCase()}
                          </Chip>
                        </div>
                        {what !== "" && (
                          <div style={{ fontSize: 13, color: "var(--fg-muted)" }}>{what}</div>
                        )}
                        <div
                          style={{
                            display: "flex",
                            gap: 14,
                            flexWrap: "wrap",
                            marginTop: 8,
                            fontSize: 12,
                            color: "var(--fg-dim)",
                          }}
                        >
                          <span>
                            Requested by <Hash value={p.proposedByOwnerId} chars={6} />
                          </span>
                          <span>Requested {shortWhen(p.createdAt)}</span>
                          <span>Expires {shortWhen(p.expiresAt)}</span>
                          <span>Threshold {p.thresholdRequired}</span>
                        </div>
                      </div>

                      {/* REVIEW is the card's action, and the only one.
                          Approving requires an EIP-712 signature over
                          typed data the server mints per proposal, and the
                          detail page is where that gate — and the redacted
                          payload the operator is signing over — actually
                          lives. A one-click Approve here would be a
                          signature over something nobody read. There is no
                          reject endpoint at all. */}
                      <Link
                        to={`/approvals/${p.id}`}
                        className="g-row"
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 8,
                          minHeight: 44,
                          padding: "10px 18px",
                          borderRadius: "var(--r-sm)",
                          border: "1px solid var(--accent)",
                          background: "var(--accent-bg)",
                          color: "var(--accent)",
                          fontWeight: 640,
                          fontSize: 13.5,
                          textDecoration: "none",
                          flex: "0 0 auto",
                        }}
                      >
                        Review
                        <IconArrowRight size={15} />
                      </Link>
                    </div>

                    <Disclosure summary="Technical details">
                      <div style={{ paddingTop: 10 }}>
                        <KvList dense>
                          <Kv k="Proposal id">
                            <code className="g-mono">{p.id}</code>
                          </Kv>
                          <Kv k="Action digest">
                            <Hash value={p.actionDigest} />
                          </Kv>
                          <Kv k="Payload hash">
                            <Hash value={p.payloadHash} />
                          </Kv>
                          <Kv k="Created">
                            <code className="g-mono">{p.createdAt}</code>
                          </Kv>
                          <Kv k="Expires">
                            <code className="g-mono">{p.expiresAt}</code>
                          </Kv>
                        </KvList>
                      </div>
                    </Disclosure>
                  </Card>
                );
              })}
            </Stack>
          )}

          {/* The route, the auth model and the session identity. All three
              are operational evidence rather than operator-facing facts —
              they answer "why is this failing", not "what should I do" — so
              they sit at the foot of the page rather than above the queue. */}
          <div style={{ marginTop: 18 }}>
            <Disclosure summary="Technical details">
              <div style={{ paddingTop: 10 }}>
                <KvList dense>
                  <Kv k="Bound to">
                    <code className="g-mono">GET /v2/admin/bff/approvals</code>
                  </Kv>
                  <Kv k="Auth">
                    Bearer session + trusted device. The browser never carries a shared secret.
                  </Kv>
                  <Kv k="Session owner">
                    <code className="g-mono">{data.sessionOwnerIdentityId}</code>
                  </Kv>
                  <Kv k="Session wallet">
                    <code className="g-mono" style={{ wordBreak: "break-all" }}>
                      {data.sessionWalletAddress}
                    </code>
                  </Kv>
                </KvList>
              </div>
            </Disclosure>
          </div>
        </>
      )}
    </div>
  );
}

export function Approvals() {
  return (
    <div>
      <PageHeader
        title="Approvals"
        subtitle="Actions waiting on an owner signature."
      />
      <AuthRequired>
        <ApprovalsInner />
      </AuthRequired>
    </div>
  );
}
